import type { Company, Notice, UpdateNoticePayload } from "@dutyon/apis";
import { getCompanies, getNotice, mapClientError, queryKeys, updateNotice } from "@dutyon/apis";
import { Card, Group, Loader, notifications, TitleContainer } from "@dutyon/ui-kit";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { NoticeForm, type NoticeFormPayload } from "../../components/NoticeForm";

interface Props {
  target: "APP" | "DASHBOARD";
}

export function NoticeEditPage({ target }: Props) {
  const { id: routeId } = useParams<{ id: string }>();
  const id = routeId ?? "";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const targetLabel = target === "APP" ? "앱" : "대시보드";
  const listPath = target === "APP" ? "/notices/app" : "/notices/dashboard";

  // 병원 목록 조회 (공지 연결용)
  const { data: companies = [] } = useQuery<Company[]>({
    queryKey: queryKeys.companies.all,
    queryFn: async () => {
      const list = await getCompanies();
      return list;
    },
  });

  // 수정할 공지사항 단건 조회
  const { data: notice, isLoading: isNoticeLoading } = useQuery<Notice | null>({
    queryKey: queryKeys.notices.detail(id),
    queryFn: () => getNotice(id),
    enabled: Boolean(id),
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; data: UpdateNoticePayload }) =>
      updateNotice(vars.id, vars.data),
    onSuccess: () => {
      notifications.show({
        title: "성공",
        message: "공지사항이 수정되었습니다.",
        color: "green",
      });
      queryClient.invalidateQueries({ queryKey: queryKeys.notices.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.notices.detail(id) });
      navigate(listPath);
    },
    onError: (error: unknown) => {
      notifications.show({
        title: "실패",
        message: mapClientError(error, {
          fallback: "공지사항 수정 중 오류가 발생했습니다.",
        }).message,
        color: "red",
      });
    },
  });

  const handleSubmit = (payload: NoticeFormPayload) => {
    if (id) {
      updateMutation.mutate({ id, data: payload });
    }
  };

  if (isNoticeLoading) {
    return (
      <TitleContainer size="md" title={`${targetLabel} 공지사항 수정`}>
        <Group justify="center" py="xl">
          <Loader size="lg" />
        </Group>
      </TitleContainer>
    );
  }

  return (
    <TitleContainer size="md" title={`${targetLabel} 공지사항 수정`}>
      <Card withBorder p="xl">
        <NoticeForm
          target={target}
          initialData={notice}
          companies={companies}
          onSubmit={handleSubmit}
          loading={updateMutation.isPending}
          onCancel={() => navigate(listPath)}
        />
      </Card>
    </TitleContainer>
  );
}
